import { useState, useEffect, useRef } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabase";
import TrialBanner from "../components/TrialBanner";
import OnboardingTour from "../components/OnboardingTour";
import { usePendingRequests } from "../hooks/usePendingRequests";
import { usePendingHandovers } from "../hooks/usePendingHandovers";
import {
  DashboardIcon, AgentsIcon, ProductsIcon, TransactionsIcon,
  ReportsIcon, CashIcon, RequestsIcon, BillingIcon,
  SunIcon, MoonIcon, PowerIcon, MenuIcon,
  ChevronLeftIcon, ChevronRightIcon, SettingsIcon,
} from "../components/Icons";

function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setW(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return w;
}

// Each nav item gets its own accent colour for the icon container
const NAV_ITEMS = [
  { path: "/dashboard",    Icon: DashboardIcon,    label: "Dashboard",      color: "#6366f1" },
  { path: "/agents",       Icon: AgentsIcon,       label: "Agents & Shops", color: "#8b5cf6" },
  { path: "/products",     Icon: ProductsIcon,     label: "Products",       color: "#38bdf8" },
  { path: "/transactions", Icon: TransactionsIcon, label: "Transactions",   color: "#10b981" },
  { path: "/reports",      Icon: ReportsIcon,      label: "Reports",        color: "#fb7185" },
  { path: "/CashTracker",  Icon: CashIcon,         label: "Cash Tracker",   color: "#f59e0b" },
  { path: "/requests",     Icon: RequestsIcon,     label: "Requests",       color: "#f87171" },
  { path: "/billing",      Icon: BillingIcon,      label: "Billing",        color: "#fbbf24" },
];

export default function OwnerMainLayout() {
  const { theme, isDark, toggleTheme } = useTheme();
  const { profile, signOut } = useAuth();
  const navigate             = useNavigate();
  const location             = useLocation();
  const width                = useWindowWidth();
  const isMobile             = width < 768;

  const pendingRequests  = usePendingRequests(profile?.id);
  const pendingHandovers = usePendingHandovers(profile?.id);

  // Presence broadcast
  const presenceCh = useRef<ReturnType<typeof supabase.channel> | null>(null);
  useEffect(() => {
    if (!profile?.id) return;
    const ch = supabase.channel("system-presence", {
      config: { presence: { key: profile.id } },
    });
    ch.subscribe(async (status) => {
      if (status === "SUBSCRIBED") {
        await ch.track({
          user_id:       profile.id,
          name:          profile.name ?? "Owner",
          business_name: (profile as any).business_name ?? null,
          role:          profile.role,
          online_at:     new Date().toISOString(),
        });
      }
    });
    presenceCh.current = ch;
    return () => { supabase.removeChannel(ch); };
  }, [profile?.id]);

  const [collapsed,      setCollapsed]      = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showLogout,     setShowLogout]     = useState(false);
  const [loggingOut,     setLoggingOut]     = useState(false);
  const [showTour,       setShowTour]       = useState(false);
  const [refreshing,     setRefreshing]     = useState(false);
  const [fixedTip,       setFixedTip]       = useState<{ text: string; y: number } | null>(null);

  const handleRefresh = () => {
    if (refreshing) return;
    setRefreshing(true);
    // Brief visual spin then reload the current route
    setTimeout(() => { navigate(0); }, 400);
  };

  // Show tour once per owner (existing + new) — wait until profile is loaded
  useEffect(() => {
    if (profile && profile.role === "supervisor" && profile.onboarding_shown === false) {
      setShowTour(true);
    }
  }, [profile?.id]);

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);
  const timeStr = now.toLocaleTimeString("en-KE", { hour: "2-digit", minute: "2-digit" });
  const dateStr = now.toLocaleDateString("en-KE", { weekday: "short", day: "numeric", month: "short" });

  const sidebarWidth = collapsed ? 68 : 248;

  const handleLogout = async () => {
    setLoggingOut(true);
    await signOut();
    navigate("/login");
  };

  const handleNav = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const isActive = (path: string) => location.pathname === path;

  const initials = profile?.business_name
    ? profile.business_name.split(" ").map((w: string) => w[0]).slice(0, 2).join("").toUpperCase()
    : "OW";

  // The current page's accent colour (for the mobile top-bar dot)
  const activePage = NAV_ITEMS.find(n => isActive(n.path));

  const RequestsBadge = ({ c }: { c: boolean }) => {
    if (pendingRequests === 0) return null;
    return c ? (
      <div style={{
        position: "absolute", top: 5, right: 5,
        width: 8, height: 8, borderRadius: "50%",
        background: "#f87171",
        boxShadow: "0 0 8px rgba(248,113,113,0.8)",
      }} />
    ) : (
      <span style={{
        background: "linear-gradient(135deg,#f87171,#ef4444)",
        color: "#fff", borderRadius: 20, padding: "2px 7px",
        fontSize: 10, fontWeight: 700,
        fontFamily: "'JetBrains Mono', monospace",
        boxShadow: "0 2px 8px rgba(239,68,68,0.4)",
      }}>
        {pendingRequests > 99 ? "99+" : pendingRequests}
      </span>
    );
  };

  return (
    <div style={{ display: "flex", minHeight: "100svh", background: theme.bg.base, fontFamily: theme.font.body, color: theme.text.primary }}>
            <style>{`
        @keyframes spin        { to{transform:rotate(360deg)} }
        @keyframes refreshSpin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes slideIn   { from{opacity:0;transform:scale(0.96) translateY(8px)}to{opacity:1;transform:scale(1) translateY(0)} }
        @keyframes slideUp   { from{opacity:0;transform:translateY(100%)}to{opacity:1;transform:translateY(0)} }
        @keyframes badgePop  { from{transform:scale(0.4);opacity:0} to{transform:scale(1);opacity:1} }
        @keyframes fadeIn    { from{opacity:0} to{opacity:1} }

        .nav-item {
          transition: background 0.15s, color 0.15s, transform 0.15s;
          border-radius: 12px;
          cursor: pointer;
        }
        .nav-item:hover {
          background: ${isDark ? "rgba(99,102,241,0.09)" : "rgba(99,102,241,0.07)"} !important;
          transform: translateX(2px);
        }
        .nav-item:focus-visible {
          outline: 2px solid ${isDark ? "rgba(99,102,241,0.6)" : "rgba(99,102,241,0.5)"};
          outline-offset: 2px;
        }
        .icon-btn:focus-visible {
          outline: 2px solid ${isDark ? "rgba(99,102,241,0.6)" : "rgba(99,102,241,0.5)"};
          outline-offset: 2px;
        }

        /* ── Collapsed icon tooltip ─────────────────────────── */
        .nav-icon-tip {
          position: relative;
          transition: transform 0.18s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.18s ease;
        }
        .nav-icon-tip:hover { transform: scale(1.15) translateX(2px); }
        .nav-icon-tip::after {
          content: attr(data-tip);
          position: absolute;
          left: calc(100% + 12px);
          top: 50%;
          transform: translateY(-50%);
          background: ${isDark ? "#1e2436" : "#ffffff"};
          border: 1px solid ${isDark ? "rgba(99,102,241,0.3)" : theme.border.default};
          color: ${isDark ? "#e2e8f0" : theme.text.primary};
          font-size: 11px;
          font-family: 'JetBrains Mono', monospace;
          font-weight: 500;
          white-space: nowrap;
          padding: 5px 10px;
          border-radius: 7px;
          box-shadow: 0 4px 16px ${isDark ? "rgba(0,0,0,0.5)" : "rgba(15,23,42,0.12)"};
          pointer-events: none;
          opacity: 0;
          transition: opacity 0.15s ease;
          z-index: 200;
        }
        .nav-icon-tip:hover::after { opacity: 1; }

        .overlay {
          position:fixed;inset:0;
          background:${isDark ? "rgba(8,10,20,0.82)" : "rgba(15,23,42,0.45)"};
          backdrop-filter:blur(8px);
          z-index:200;display:flex;
          align-items:center;justify-content:center;padding:20px;
          animation: fadeIn 0.2s ease;
        }
        .modal {
          background: ${isDark
            ? "linear-gradient(145deg, #161b27, #111827)"
            : theme.bg.card};
          border:1px solid ${isDark ? "rgba(99,102,241,0.18)" : theme.border.default};
          border-radius:20px;padding:32px;
          width:100%;max-width:380px;
          box-shadow: ${isDark
            ? "0 24px 64px rgba(0,0,0,0.5), 0 0 0 1px rgba(99,102,241,0.1)"
            : "0 24px 64px rgba(15,23,42,0.18), 0 0 0 1px " + theme.border.default};
          animation:slideIn 0.22s ease;
        }
        .mobile-drawer {
          position:fixed;inset:0;z-index:150;
          background:${isDark ? "rgba(8,10,20,0.7)" : "rgba(15,23,42,0.45)"};
          backdrop-filter:blur(4px);
        }
        .mobile-menu {
          position:fixed;bottom:0;left:0;right:0;
          background: ${isDark
            ? "linear-gradient(180deg, #161b27 0%, #0d1117 100%)"
            : "linear-gradient(180deg, " + theme.bg.card + " 0%, " + theme.bg.base + " 100%)"};
          border-top:1px solid ${isDark ? "rgba(99,102,241,0.18)" : theme.border.default};
          border-radius:22px 22px 0 0;
          padding:16px 16px env(safe-area-inset-bottom,16px);
          animation:slideUp 0.28s cubic-bezier(0.16,1,0.3,1);
          box-shadow: 0 -12px 40px ${isDark ? "rgba(0,0,0,0.4)" : "rgba(15,23,42,0.12)"};
          max-height:88svh;
          overflow-y:auto;
          overscroll-behavior:contain;
        }
        /* Landscape on small phones — slim everything down */
        @media (max-height: 500px) and (orientation: landscape) {
          .mobile-menu {
            max-height:96svh;
            padding:10px 16px env(safe-area-inset-bottom,10px);
            border-radius:16px 16px 0 0;
          }
          .mobile-menu .drag-handle { margin-bottom:8px; }
          .mobile-menu .owner-card { padding:8px 12px; margin-bottom:8px; }
          .mobile-menu .owner-avatar { width:32px;height:32px;font-size:11px; }
          .mobile-menu .owner-name { font-size:12px; }
          .mobile-menu .owner-sub { font-size:9px; }
          .mobile-menu .nav-grid { display:grid;grid-template-columns:1fr 1fr;gap:4px;margin-bottom:8px; }
          .mobile-menu .nav-item { padding:7px 10px !important; }
          .mobile-menu .nav-icon { width:24px !important;height:24px !important;border-radius:7px !important; }
          .mobile-menu .nav-label { font-size:12px !important; }
          .mobile-menu .action-row { display:grid;grid-template-columns:1fr 1fr;gap:6px; }
          .mobile-menu .action-btn { padding:8px !important;font-size:12px !important; }
        }
      `}</style>

      {/* ── DESKTOP SIDEBAR ──────────────────────────────────────── */}
      {!isMobile && (
        <aside style={{
          width: sidebarWidth, flexShrink: 0,
          background: theme.bg.sidebar,
          borderRight: `1px solid ${theme.border.default}`,
          display: "flex", flexDirection: "column",
          position: "sticky", top: 0, height: "100svh",
          transition: "width 0.22s cubic-bezier(0.4,0,0.2,1)",
          overflow: "visible", zIndex: 50,
        }}>

          {/* ── Header ───────────────────────────────────────────── */}
          {collapsed ? (
            /* Collapsed: vertical stack — logo → expand → brightness → refresh */
            <div style={{
              borderBottom: `1px solid ${theme.border.default}`,
              display: "flex", flexDirection: "column", alignItems: "center",
              gap: 6, padding: "12px 0 10px", flexShrink: 0,
            }}>
              <img src="/Qash.png" alt="Qash" style={{ width: 26, height: 26, objectFit: "contain", marginBottom: 2 }} />

              {/* Expand button — large + prominent so it's always findable */}
              <button
                onClick={() => setCollapsed(false)}
                className="icon-btn nav-icon-tip"
                data-tip="Expand sidebar"
                aria-label="Expand sidebar"
                style={{
                  width: 34, height: 34, borderRadius: 9,
                  background: "rgba(99,102,241,0.18)", border: "1px solid rgba(99,102,241,0.4)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", color: "#818cf8",
                  boxShadow: "0 0 10px rgba(99,102,241,0.2)",
                  transition: "background 0.15s, box-shadow 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(99,102,241,0.32)"; e.currentTarget.style.boxShadow = "0 0 16px rgba(99,102,241,0.45)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(99,102,241,0.18)"; e.currentTarget.style.boxShadow = "0 0 10px rgba(99,102,241,0.2)"; }}
              >
                <ChevronRightIcon size={14} />
              </button>

              {/* Brightness */}
              <button
                onClick={toggleTheme}
                className="icon-btn nav-icon-tip"
                data-tip={isDark ? "Light mode" : "Dark mode"}
                aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
                style={{
                  width: 30, height: 30, borderRadius: 8,
                  background: "rgba(234,179,8,0.12)", border: "1px solid rgba(234,179,8,0.25)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", color: "#fbbf24",
                  transition: "background 0.15s, box-shadow 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(234,179,8,0.25)"; e.currentTarget.style.boxShadow = "0 0 12px rgba(234,179,8,0.3)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(234,179,8,0.12)"; e.currentTarget.style.boxShadow = "none"; }}
              >
                {isDark ? <SunIcon size={13} /> : <MoonIcon size={13} />}
              </button>

              {/* Refresh */}
              <button
                onClick={handleRefresh}
                className="icon-btn nav-icon-tip"
                data-tip="Refresh"
                aria-label="Refresh data"
                style={{
                  width: 30, height: 30, borderRadius: 8,
                  background: "rgba(6,182,212,0.12)", border: "1px solid rgba(6,182,212,0.25)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: refreshing ? "default" : "pointer", color: "#22d3ee",
                  transition: "background 0.15s, box-shadow 0.15s",
                }}
                onMouseEnter={e => { if (!refreshing) { e.currentTarget.style.background = "rgba(6,182,212,0.25)"; e.currentTarget.style.boxShadow = "0 0 12px rgba(6,182,212,0.3)"; } }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(6,182,212,0.12)"; e.currentTarget.style.boxShadow = "none"; }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                  style={{ animation: refreshing ? "refreshSpin 0.5s linear" : "none" }}>
                  <polyline points="23 4 23 10 17 10" />
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                </svg>
              </button>
            </div>
          ) : (
            /* Expanded: single horizontal bar */
            <div style={{
              padding: "14px 14px",
              borderBottom: `1px solid ${theme.border.default}`,
              display: "flex", alignItems: "center", justifyContent: "space-between",
              gap: 8, flexShrink: 0,
            }}>
              <img src="/Qash.png" alt="Qash" style={{ width: 30, height: 30, objectFit: "contain", flexShrink: 0 }} />

              <div style={{ flex: 1, minWidth: 0, paddingLeft: 4 }}>
                <div style={{ fontFamily: theme.font.mono, fontSize: 12, color: theme.text.primary, fontWeight: 600, lineHeight: 1.2 }}>{timeStr}</div>
                <div style={{ fontFamily: theme.font.mono, fontSize: 9, color: theme.text.muted, marginTop: 1 }}>{dateStr}</div>
              </div>

              <button onClick={toggleTheme} className="icon-btn" aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"} title={isDark ? "Light mode" : "Dark mode"}
                style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(234,179,8,0.12)", border: "1px solid rgba(234,179,8,0.3)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, color: "#fbbf24", transition: "background 0.15s" }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(234,179,8,0.22)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(234,179,8,0.12)"; }}>
                {isDark ? <SunIcon size={13} /> : <MoonIcon size={13} />}
              </button>

              <button onClick={handleRefresh} className="icon-btn" aria-label="Refresh data" title="Refresh"
                style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(6,182,212,0.12)", border: "1px solid rgba(6,182,212,0.3)", display: "flex", alignItems: "center", justifyContent: "center", cursor: refreshing ? "default" : "pointer", flexShrink: 0, color: "#22d3ee", transition: "background 0.15s" }}
                onMouseEnter={e => { if (!refreshing) e.currentTarget.style.background = "rgba(6,182,212,0.22)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(6,182,212,0.12)"; }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                  style={{ animation: refreshing ? "refreshSpin 0.5s linear" : "none" }}>
                  <polyline points="23 4 23 10 17 10" />
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                </svg>
              </button>

              <button onClick={() => setCollapsed(true)} className="icon-btn" aria-label="Collapse sidebar" title="Collapse sidebar"
                style={{ width: 28, height: 28, borderRadius: 8, background: "rgba(99,102,241,0.14)", border: "1px solid rgba(99,102,241,0.3)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, color: "#818cf8", transition: "background 0.15s" }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(99,102,241,0.25)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(99,102,241,0.14)"; }}>
                <ChevronLeftIcon size={13} />
              </button>
            </div>
          )}

          {/* Nav items */}
          <nav aria-label="Main navigation" style={{ flex: 1, padding: "14px 10px", display: "flex", flexDirection: "column", gap: 3, overflowY: "auto" }}>
            {NAV_ITEMS.map(({ path, Icon, label, color }) => {
              const active = isActive(path);
              const isRequests    = path === "/requests";
              const isCashTracker = path === "/CashTracker";
              return (
                <div
                  key={path}
                  role="button"
                  tabIndex={0}
                  className="nav-item"
                  data-tour={path.replace("/", "")}
                  onClick={() => handleNav(path)}
                  onKeyDown={e => e.key === "Enter" && handleNav(path)}
                  aria-current={active ? "page" : undefined}
                  aria-label={label}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: collapsed ? "10px 0" : "9px 12px",
                    justifyContent: collapsed ? "center" : "flex-start",
                    background: active
                      ? `linear-gradient(135deg, ${color}22, ${color}10)`
                      : "transparent",
                    border: `1px solid ${active ? color + "33" : "transparent"}`,
                    position: "relative",
                    transform: "translateX(0)",  // baseline for hover override
                  }}>

                  {/* Coloured icon container — tooltip + glow when collapsed */}
                  <div
                    style={{
                      width: 30, height: 30, borderRadius: 9, flexShrink: 0,
                      background: active ? color + "22" : "rgba(255,255,255,0.04)",
                      border: `1px solid ${active ? color + "44" : "rgba(255,255,255,0.06)"}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      transition: "background 0.15s, border-color 0.15s, box-shadow 0.18s, transform 0.18s cubic-bezier(0.34,1.56,0.64,1)",
                      position: "relative",
                      boxShadow: active && collapsed ? `0 0 10px ${color}55` : "none",
                    }}
                    onMouseEnter={e => {
                      if (collapsed) {
                        const el = e.currentTarget as HTMLElement;
                        el.style.boxShadow = `0 0 14px ${color}70`;
                        el.style.background = color + "30";
                        el.style.borderColor = color + "66";
                        el.style.transform = "scale(1.15) translateX(2px)";
                        const rect = el.getBoundingClientRect();
                        setFixedTip({ text: label, y: rect.top + rect.height / 2 });
                      }
                    }}
                    onMouseLeave={e => {
                      if (collapsed) {
                        const el = e.currentTarget as HTMLElement;
                        el.style.boxShadow = active ? `0 0 10px ${color}55` : "none";
                        el.style.background = active ? color + "22" : "rgba(255,255,255,0.04)";
                        el.style.borderColor = active ? color + "44" : "rgba(255,255,255,0.06)";
                        el.style.transform = "";
                        setFixedTip(null);
                      }
                    }}
                  >
                    <Icon size={15} color={active ? color : theme.text.muted} style={{ flexShrink: 0 }} />
                    {isRequests    && <RequestsBadge c={true} />}
                    {isCashTracker && pendingHandovers > 0 && (
                      <div style={{
                        position: "absolute", top: 4, right: 4,
                        width: 8, height: 8, borderRadius: "50%",
                        background: "#f59e0b",
                        boxShadow: "0 0 8px rgba(245,158,11,0.8)",
                      }} />
                    )}
                  </div>

                  {!collapsed && (
                    <>
                      <span style={{
                        fontSize: 13, fontWeight: active ? 600 : 400,
                        color: active ? color : theme.text.secondary,
                        whiteSpace: "nowrap", flex: 1,
                        transition: "color 0.15s",
                      }}>
                        {label}
                      </span>
                      {isRequests && pendingRequests > 0 && !collapsed && (
                        <RequestsBadge c={false} />
                      )}
                      {isCashTracker && pendingHandovers > 0 && !collapsed && (
                        <span style={{
                          background: "linear-gradient(135deg,#f59e0b,#d97706)",
                          color: "#000", borderRadius: 20, padding: "2px 7px",
                          fontSize: 10, fontWeight: 700,
                          fontFamily: "'JetBrains Mono', monospace",
                          boxShadow: "0 2px 8px rgba(245,158,11,0.4)",
                        }}>
                          {pendingHandovers > 99 ? "99+" : pendingHandovers}
                        </span>
                      )}
                      {active && !isRequests && !isCashTracker && (
                        <div style={{ width: 5, height: 5, borderRadius: "50%", background: color, boxShadow: `0 0 6px ${color}` }} />
                      )}
                      {active && isCashTracker && pendingHandovers === 0 && (
                        <div style={{ width: 5, height: 5, borderRadius: "50%", background: color, boxShadow: `0 0 6px ${color}` }} />
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Owner profile + sign-out */}
          <div style={{ padding: "12px 10px", borderTop: `1px solid ${theme.border.default}` }}>
            {/* Profile row */}
            <div
              onClick={() => navigate("/settings")}
              className="nav-item"
              data-tour="settings"
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: collapsed ? "10px 0" : "10px 12px",
                justifyContent: collapsed ? "center" : "flex-start",
                marginBottom: 4,
                background: "rgba(99,102,241,0.06)",
                border: "1px solid rgba(99,102,241,0.14)",
              }}
              title="Go to Settings"
            >
              {/* Avatar with gradient ring */}
              <div
                style={{ position: "relative", flexShrink: 0 }}
                onMouseEnter={e => {
                  if (collapsed) {
                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    setFixedTip({ text: profile?.business_name || "Settings", y: rect.top + rect.height / 2 });
                  }
                }}
                onMouseLeave={() => collapsed && setFixedTip(null)}
              >
                <div style={{
                  width: 30, height: 30, borderRadius: "50%",
                  background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: theme.font.display, fontWeight: 700, fontSize: 11,
                  color: "#fff",
                  boxShadow: "0 0 0 2px rgba(99,102,241,0.4)",
                }}>
                {initials}
                </div>
                {/* Online dot */}
                <div style={{
                  position: "absolute", bottom: 0, right: 0,
                  width: 8, height: 8, borderRadius: "50%",
                  background: "#10b981",
                  border: `2px solid ${theme.bg.sidebar}`,
                  boxShadow: "0 0 6px rgba(16,185,129,0.6)",
                }} />
              </div>
              {!collapsed && (
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {profile?.business_name || "Owner"}
                  </div>
                  <div style={{ fontSize: 9, fontFamily: theme.font.mono, color: "#6366f1", marginTop: 1, display: "flex", alignItems: "center", gap: 3 }}>
                    <SettingsIcon size={9} color="#6366f1" /> Settings
                  </div>
                </div>
              )}
              {!collapsed && <ChevronRightIcon size={12} color={theme.text.muted} style={{ opacity: 0.5, flexShrink: 0 }} />}
            </div>

            {/* Tour replay */}
            <button
              onClick={() => setShowTour(true)}
              className="nav-item icon-btn"
              aria-label="Take a tour"
              title="Take a tour"
              style={{
                width: "100%", background: "none", border: "none",
                display: "flex", alignItems: "center", gap: 10,
                padding: collapsed ? "10px 0" : "9px 12px",
                justifyContent: collapsed ? "center" : "flex-start",
                color: "#a78bfa", cursor: "pointer", marginBottom: 4,
              }}>
              <div
                style={{
                  width: 30, height: 30, borderRadius: 9, flexShrink: 0,
                  background: "rgba(167,139,250,0.08)",
                  border: "1px solid rgba(167,139,250,0.18)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 14,
                }}
                onMouseEnter={e => {
                  if (collapsed) {
                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    setFixedTip({ text: "Take a Tour", y: rect.top + rect.height / 2 });
                  }
                }}
                onMouseLeave={() => collapsed && setFixedTip(null)}
              >
                🧭
              </div>
              {!collapsed && <span style={{ fontSize: 13, whiteSpace: "nowrap" }}>Take a Tour</span>}
            </button>

            {/* Sign out */}
            <button
              onClick={() => setShowLogout(true)}
              className="nav-item icon-btn"
              aria-label="Sign out"
              style={{
                width: "100%", background: "none", border: "none",
                display: "flex", alignItems: "center", gap: 10,
                padding: collapsed ? "10px 0" : "9px 12px",
                justifyContent: collapsed ? "center" : "flex-start",
                color: theme.accent.red, cursor: "pointer",
              }}>
              <div
                style={{
                  width: 30, height: 30, borderRadius: 9, flexShrink: 0,
                  background: "rgba(248,113,113,0.08)",
                  border: "1px solid rgba(248,113,113,0.15)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
                onMouseEnter={e => {
                  if (collapsed) {
                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    setFixedTip({ text: "Sign Out", y: rect.top + rect.height / 2 });
                  }
                }}
                onMouseLeave={() => collapsed && setFixedTip(null)}
              >
                <PowerIcon size={14} color={theme.accent.red} />
              </div>
              {!collapsed && <span style={{ fontSize: 13, whiteSpace: "nowrap" }}>Sign Out</span>}
            </button>
          </div>

          {fixedTip && collapsed && (
            <div style={{
              position: "fixed",
              left: 76,
              top: fixedTip.y,
              transform: "translateY(-50%)",
              zIndex: 9999,
              background: isDark ? "#1e2436" : "#ffffff",
              border: `1px solid ${isDark ? "rgba(99,102,241,0.35)" : theme.border.default}`,
              color: isDark ? "#e2e8f0" : theme.text.primary,
              fontSize: 11,
              fontFamily: "'JetBrains Mono', monospace",
              whiteSpace: "nowrap",
              padding: "5px 10px",
              borderRadius: 7,
              boxShadow: isDark ? "0 4px 16px rgba(0,0,0,0.55)" : "0 4px 16px rgba(15,23,42,0.15)",
              pointerEvents: "none",
            }}>
              {fixedTip.text}
            </div>
          )}
        </aside>
      )}

      {/* ── MAIN CONTENT ─────────────────────────────────────────── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, overflow: "hidden" }}>
        <TrialBanner />

        {/* Mobile top bar */}
        {isMobile && (
          <div style={{
            position: "sticky",
            top: 0,
            zIndex: 40,
            background: `${theme.bg.sidebar}f0`,
            backdropFilter: "blur(12px)",
            borderBottom: `1px solid ${theme.border.default}`,
            paddingTop: "calc(13px + env(safe-area-inset-top))",
            paddingRight: "16px",
            paddingBottom: "13px",
            paddingLeft: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <img src="/Qash.png" alt="Qash" style={{ width: 32, height: 32, objectFit: "contain" }} />
              {activePage && (
                <div style={{ fontSize: 9, color: activePage.color, fontFamily: theme.font.mono, letterSpacing: "0.05em" }}>
                  {activePage.label.toUpperCase()}
                </div>
              )}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                <span style={{ fontFamily: theme.font.mono, fontSize: 11, color: theme.text.primary, fontWeight: 500 }}>{timeStr}</span>
                <span style={{ fontFamily: theme.font.mono, fontSize: 9, color: theme.text.muted }}>{dateStr}</span>
              </div>
              {/* Refresh — mobile */}
              <button
                onClick={handleRefresh}
                className="icon-btn"
                aria-label="Refresh data"
                style={{
                  width: 32, height: 32, borderRadius: 9,
                  background: "rgba(6,182,212,0.12)", border: "1px solid rgba(6,182,212,0.3)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: refreshing ? "default" : "pointer", color: "#22d3ee",
                }}>
                <svg
                  width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                  style={{ animation: refreshing ? "refreshSpin 0.5s linear" : "none" }}
                >
                  <polyline points="23 4 23 10 17 10" />
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                </svg>
              </button>

              {/* Theme toggle — mobile, amber tint */}
              <button
                onClick={toggleTheme}
                className="icon-btn"
                style={{
                  width: 32, height: 32, borderRadius: 9,
                  background: "rgba(234,179,8,0.12)", border: "1px solid rgba(234,179,8,0.3)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", color: "#fbbf24",
                }}>
                {isDark ? <SunIcon size={14} /> : <MoonIcon size={14} />}
              </button>
              <div style={{ position: "relative", display: "inline-flex" }}>
                <button
                  onClick={() => setMobileMenuOpen(true)}
                  className="icon-btn"
                  aria-label="Open navigation menu"
                  style={{
                    background: "rgba(99,102,241,0.08)", border: `1px solid ${theme.border.default}`,
                    borderRadius: 9, padding: "7px 10px",
                    color: theme.text.primary, cursor: "pointer", display: "flex", alignItems: "center",
                  }}>
                  <MenuIcon size={18} />
                </button>
                {pendingRequests > 0 && (
                  <div style={{
                    position: "absolute", top: -4, right: -4,
                    minWidth: 16, height: 16, borderRadius: 20,
                    background: "linear-gradient(135deg,#f87171,#ef4444)", color: "#fff",
                    fontSize: 9, fontWeight: 700, fontFamily: "monospace",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: "0 4px", border: `2px solid ${theme.bg.sidebar}`,
                    boxShadow: "0 0 8px rgba(248,113,113,0.6)",
                    animation: "badgePop 0.25s ease",
                  }}>
                    {pendingRequests > 99 ? "99+" : pendingRequests}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <main style={{ flex: 1, overflowY: "auto", overflowX: "hidden", paddingBottom: isMobile ? 20 : 0 }}>
          <Outlet />
        </main>
      </div>

      {/* ── MOBILE DRAWER MENU ───────────────────────────────────── */}
      {isMobile && mobileMenuOpen && (
        <div className="mobile-drawer" onClick={() => setMobileMenuOpen(false)}>
          <div className="mobile-menu" onClick={e => e.stopPropagation()}>
            {/* Drag handle */}
            <div className="drag-handle" style={{ width: 40, height: 4, borderRadius: 2, background: "rgba(99,102,241,0.3)", margin: "0 auto 16px" }} />

            {/* Owner info */}
            <div
              className="owner-card"
              onClick={() => { setMobileMenuOpen(false); navigate("/settings"); }}
              style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "12px 14px",
                background: "rgba(99,102,241,0.08)",
                border: "1px solid rgba(99,102,241,0.18)",
                borderRadius: 14, marginBottom: 12, cursor: "pointer",
              }}>
              <div style={{ position: "relative", flexShrink: 0 }}>
                <div className="owner-avatar" style={{
                  width: 40, height: 40, borderRadius: "50%",
                  background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: theme.font.display, fontWeight: 700, fontSize: 14, color: "#fff",
                  boxShadow: "0 0 0 3px rgba(99,102,241,0.3)",
                }}>
                  {initials}
                </div>
                <div style={{
                  position: "absolute", bottom: 0, right: 0,
                  width: 10, height: 10, borderRadius: "50%",
                  background: "#10b981", border: `2px solid ${isDark ? "#0d1117" : theme.bg.card}`,
                  boxShadow: "0 0 6px rgba(16,185,129,0.6)",
                }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="owner-name" style={{ fontWeight: 600, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {profile?.business_name || "Owner"}
                </div>
                <div className="owner-sub" style={{ fontSize: 10, fontFamily: theme.font.mono, color: "#6366f1", marginTop: 2, display: "flex", alignItems: "center", gap: 3 }}>
                  <SettingsIcon size={9} color="#6366f1" /> Tap to open settings
                </div>
              </div>
              <span style={{ fontSize: 18, opacity: 0.4, color: theme.text.muted }}>›</span>
            </div>

            {/* Nav items — 2-col grid in landscape via CSS class */}
            <nav aria-label="Main navigation" className="nav-grid" style={{ display: "flex", flexDirection: "column", gap: 3, marginBottom: 12 }}>
              {NAV_ITEMS.map(({ path, Icon, label, color }) => {
                const active        = isActive(path);
                const isRequests    = path === "/requests";
                const isCashTracker = path === "/CashTracker";
                return (
                  <div
                    key={path}
                    role="button"
                    tabIndex={0}
                    className="nav-item"
                    onClick={() => handleNav(path)}
                    onKeyDown={e => e.key === "Enter" && handleNav(path)}
                    aria-current={active ? "page" : undefined}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "10px 12px",
                      background: active ? `${color}18` : "transparent",
                      border: `1px solid ${active ? color + "30" : "transparent"}`,
                    }}>
                    <div className="nav-icon" style={{
                      width: 30, height: 30, borderRadius: 9, flexShrink: 0,
                      background: active ? `${color}22` : "rgba(255,255,255,0.04)",
                      border: `1px solid ${active ? color + "40" : "rgba(255,255,255,0.06)"}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <Icon size={15} color={active ? color : theme.text.muted} />
                    </div>
                    <span className="nav-label" style={{ fontSize: 13, fontWeight: active ? 600 : 400, color: active ? color : theme.text.secondary, flex: 1 }}>
                      {label}
                    </span>
                    {isRequests && pendingRequests > 0 && (
                      <span style={{
                        background: "linear-gradient(135deg,#f87171,#ef4444)", color: "#fff",
                        borderRadius: 20, padding: "2px 7px",
                        fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace",
                        boxShadow: "0 2px 8px rgba(239,68,68,0.35)",
                      }}>
                        {pendingRequests > 99 ? "99+" : pendingRequests}
                      </span>
                    )}
                    {isCashTracker && pendingHandovers > 0 && (
                      <span style={{
                        background: "linear-gradient(135deg,#f59e0b,#d97706)", color: "#000",
                        borderRadius: 20, padding: "2px 7px",
                        fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace",
                        boxShadow: "0 2px 8px rgba(245,158,11,0.35)",
                      }}>
                        {pendingHandovers > 99 ? "99+" : pendingHandovers}
                      </span>
                    )}
                    {active && !isRequests && !isCashTracker && (
                      <div style={{ width: 6, height: 6, borderRadius: "50%", background: color, boxShadow: `0 0 8px ${color}`, flexShrink: 0 }} />
                    )}
                    {active && isCashTracker && pendingHandovers === 0 && (
                      <div style={{ width: 6, height: 6, borderRadius: "50%", background: color, boxShadow: `0 0 8px ${color}`, flexShrink: 0 }} />
                    )}
                  </div>
                );
              })}
            </nav>

            {/* Tour + Sign out */}
            <div className="action-row" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <button
                className="action-btn"
                onClick={() => { setMobileMenuOpen(false); setShowTour(true); }}
                style={{
                  width: "100%",
                  background: "rgba(167,139,250,0.08)",
                  border: "1px solid rgba(167,139,250,0.2)",
                  borderRadius: 12, padding: "11px",
                  color: "#a78bfa", fontFamily: theme.font.mono,
                  fontSize: 13, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}>
                🧭 Take a Tour
              </button>

              <button
                className="action-btn"
                onClick={() => { setMobileMenuOpen(false); setShowLogout(true); }}
                style={{
                  width: "100%",
                  background: "rgba(248,113,113,0.08)",
                  border: "1px solid rgba(248,113,113,0.2)",
                  borderRadius: 12, padding: "11px",
                  color: theme.accent.red, fontFamily: theme.font.mono,
                  fontSize: 13, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}>
                <PowerIcon size={14} color={theme.accent.red} /> Sign Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ONBOARDING TOUR ─────────────────────────────────────── */}
      {showTour && profile && (
        <OnboardingTour
          ownerId={profile.id}
          businessName={(profile as any).business_name ?? profile.name}
          onDone={() => setShowTour(false)}
        />
      )}

      {/* ── LOGOUT CONFIRMATION MODAL ────────────────────────────── */}
      {showLogout && (
        <div className="overlay" onClick={() => !loggingOut && setShowLogout(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 18 }}>
                <div style={{
                  width: 56, height: 56, borderRadius: "50%",
                  background: "linear-gradient(135deg, rgba(248,113,113,0.15), rgba(239,68,68,0.08))",
                  border: "1px solid rgba(248,113,113,0.3)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: "0 0 24px rgba(248,113,113,0.2)",
                }}>
                  <PowerIcon size={24} color={theme.accent.red} />
                </div>
              </div>
              <div style={{ fontFamily: theme.font.display, fontWeight: 800, fontSize: 22, letterSpacing: "-0.3px" }}>Sign out?</div>
              <div style={{ color: theme.text.muted, fontSize: 13, fontFamily: theme.font.body, marginTop: 10, lineHeight: 1.7 }}>
                You'll be returned to the login screen.<br />
                <strong style={{ color: theme.text.primary }}>{profile?.name || "Owner"}</strong> will need to sign in again.
              </div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => setShowLogout(false)}
                disabled={loggingOut}
                style={{
                  flex: 1, background: "rgba(255,255,255,0.04)",
                  border: `1px solid ${theme.border.default}`,
                  borderRadius: 12, padding: 14,
                  color: theme.text.secondary, fontFamily: theme.font.body,
                  fontSize: 14, cursor: "pointer",
                  transition: "background 0.15s",
                }}>
                Cancel
              </button>
              <button
                onClick={handleLogout}
                disabled={loggingOut}
                style={{
                  flex: 2,
                  background: loggingOut
                    ? "rgba(248,113,113,0.25)"
                    : "linear-gradient(135deg, #ef4444, #dc2626)",
                  border: "none", borderRadius: 12, padding: 14,
                  color: "#fff", fontFamily: theme.font.display,
                  fontWeight: 700, fontSize: 15,
                  cursor: loggingOut ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  boxShadow: loggingOut ? "none" : "0 4px 16px rgba(239,68,68,0.4)",
                  transition: "box-shadow 0.15s",
                }}>
                {loggingOut
                  ? <><span style={{ width: 15, height: 15, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />Signing out...</>
                  : "Sign Out"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
